import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import SortByFilterModal from './SortByFilterModal';
import SearchInFilterModal from './SearchInFilterModal';
import {
  BaseButton,
  ToggleButton,
  HorizonLine,
  Checkbox,
  useT,
} from '@repo/common';

import { getQnaCategories } from '../../../api/qna/qnaCategory.api';
import { getSearchQna } from '../../../api/qna/qnaSearch.api';

const QnaSearchSide = ({
  showCategory = true,
  qnaDetail = false,
  activeCategory,
  setActiveCategory,
  filterState,
  setFilterState,
  appliedFilterState,
  onApply,
}) => {
  const t = useT();
  const containerRef = useRef(null);
  const [isFilterDirty, setIsFilterDirty] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isSortFilterModalOpen, setIsSortFilterModalOpen] = useState(false);
  const [isSearchFilterModalOpen, setIsSearchFilterModalOpen] = useState(false);
  const [checkedCategories, setCheckedCategories] = useState({});

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const keyword = params.get('keyword');

  useEffect(() => {
    if (qnaDetail) return;
    const handleScroll = () => {
      if (!containerRef.current) return;

      if (isSortFilterModalOpen) {
        setIsSortFilterModalOpen(false);
      }

      if (isSearchFilterModalOpen) {
        setIsSearchFilterModalOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [qnaDetail, isSortFilterModalOpen, isSearchFilterModalOpen]);

  const handleScroll = () => {
    if (!containerRef.current) return;
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isClickInsideModal = e.target.closest('.filter-modal-container');
      const isClickOnSortButton = e.target.closest('.sort-filter-button');
      const isClickOnSearchInButton = e.target.closest('.search-filter-button');

      if (
        !isClickInsideModal &&
        !isClickOnSortButton &&
        !isClickOnSearchInButton
      ) {
        setIsSortFilterModalOpen(false);
        setIsSearchFilterModalOpen(false);
      }
    };

    if (isSortFilterModalOpen || isSearchFilterModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortFilterModalOpen, isSearchFilterModalOpen]);

  useEffect(() => {
    if (!showCategory) return;
    const fetchCategories = async () => {
      const data = await getQnaCategories({ includeAll: true });
      setCategories(data);

      const allCategory = data.find((category) => category.name === 'All');

      if (allCategory) {
        setActiveCategory(allCategory.id);
      }
    };

    fetchCategories();
  }, [setActiveCategory, showCategory]);

  useEffect(() => {
    const isCategoryChecked = Object.values(checkedCategories).some((v) => v);

    const dirty =
      filterState.sortBy !== appliedFilterState.sortBy ||
      filterState.searchIn !== appliedFilterState.searchIn ||
      filterState.onlyAnswered !== appliedFilterState.onlyAnswered ||
      isCategoryChecked;

    setIsFilterDirty(dirty);
  }, [filterState, checkedCategories, appliedFilterState]);

  const clearFilters = () => {
    const defaultState = {
      sortBy: 'Most Relevant',
      searchIn: 'All',
      onlyAnswered: false,
    };

    setFilterState(defaultState);
    onApply?.(defaultState);

    setActiveCategory(null);
    setCheckedCategories({});
  };

  const handleCategoryCheck = (categoryId) => {
    setCheckedCategories((prev) => {
      if (categoryId === 1) {
        if (prev[1]) {
          return {};
        }
        return { 1: true };
      }

      if (prev[1]) {
        return { [categoryId]: true };
      }

      return { ...prev, [categoryId]: !prev[categoryId] };
    });

    setActiveCategory(categoryId);
  };

  const handleApplyClick = async () => {
    const categoryIds = Object.keys(checkedCategories)
      .filter((key) => checkedCategories[key] === true)
      .map(Number);

    const result = await getSearchQna({
      keyword,
      page: 0,
      sort: filterState.sortBy,
      searchIn: filterState.searchIn,
      onlyAnswered: filterState.onlyAnswered,
      categoryIds,
    });

    onApply?.({
      filters: filterState,
      category: activeCategory,
      searchResult: result,
    });
  };

  return (
    <div ref={containerRef} className='w-[273px] flex flex-col'>
      <div className='px-6 py-[30px] bg-grayscale-gray00 border-[1.6px] border-grayscale-gray02 rounded-[30px]'>
        {/* Q&A 필터 헤더 */}
        <div className='flex justify-between gap-2 pb-6 typo-h5'>
          {t('Filters', '필터')}
        </div>

        <HorizonLine />

        <div className='flex flex-col justify-between gap-3 py-6 typo-b18Strong'>
          {t('Sort by', '정렬 기준')}
          <SortByFilterModal
            initialSort={filterState.sortBy}
            appliedSort={appliedFilterState.sortBy}
            isOpen={isSortFilterModalOpen}
            setIsOpen={(value) => {
              setIsSortFilterModalOpen(value);
              if (value) setIsSearchFilterModalOpen(false);
            }}
            onChange={(newSort) => {
              setFilterState((prev) => ({ ...prev, sortBy: newSort }));
            }}
          />
        </div>

        <HorizonLine />

        <div className='flex flex-col justify-between gap-3 py-6 typo-b18Strong'>
          {t('Search in', '검색 범위')}
          <SearchInFilterModal
            initialSearchIn={filterState.searchIn}
            appliedSearchIn={appliedFilterState.searchIn}
            isOpen={isSearchFilterModalOpen}
            setIsOpen={(value) => {
              setIsSearchFilterModalOpen(value);
              if (value) setIsSortFilterModalOpen(false);
            }}
            onChange={(newSearchIn) => {
              setFilterState((prev) => ({ ...prev, searchIn: newSearchIn }));
            }}
          />
        </div>

        <HorizonLine />

        <div className='flex justify-between py-6 typo-b14Strong'>
          {t("With tutor's answer only", '튜터 답변이 있는 질문만')}
          <ToggleButton
            initialSort={filterState.onlyAnswered}
            onChange={(value) =>
              setFilterState((prev) => ({ ...prev, onlyAnswered: value }))
            }
          />
        </div>

        <HorizonLine />

        {/* Category 섹션 */}
        {showCategory && (
          <div className='py-6'>
            <div className='typo-b18Strong text-grayscale-gray09 pb-4'>
              {t('Category', '카테고리')}
            </div>

            <nav className='flex flex-col'>
              {categories.map((category) => {
                const isAllSelected = checkedCategories[1] === true;
                const isThisCategoryAll = category.id === 1;

                const shouldLookDisabled = isAllSelected && !isThisCategoryAll;

                return (
                  <div
                    key={category.id}
                    className={`flex justify-between py-[10px] ${
                      shouldLookDisabled
                        ? 'text-text-disable'
                        : 'text-text-tertiary'
                    }`}
                  >
                    {category.name}
                    <Checkbox
                      base='placeholder'
                      checked={checkedCategories[category.id] ?? false}
                      onChange={() => handleCategoryCheck(category.id)}
                      disabled={shouldLookDisabled}
                    />
                  </div>
                );
              })}
            </nav>
          </div>
        )}
        <HorizonLine />
        <div className='flex flex-col gap-2  pt-[60px]'>
          <BaseButton
            hideIcon
            isDisabled={!isFilterDirty}
            onClick={handleApplyClick}
          >
            {t('Apply filters', '필터 적용')}
          </BaseButton>
          <BaseButton
            type='secondary'
            hideIcon
            isDisabled={!isFilterDirty}
            onClick={clearFilters}
          >
            {t('Clear all', '전체 초기화')}
          </BaseButton>
        </div>
      </div>
    </div>
  );
};

export default QnaSearchSide;
